import React, { useCallback, useEffect, useState } from "react"
import { Alert, Skeleton } from "antd"
import SharedVirtualMeetInvitationCard, {
  ISharedVirtualMeetInvitationCardData,
} from "./SharedVirtualMeetInvitationCard"
import { gen, swimminglyApi } from "./utils"

const ACTIVITY_FEED_REFETCH_EVENT = "swimmingly:activity-feed:refetch"

export default function VirtualMeetInvitationPreview({
  item,
}: {
  item: {
    clubId?: number | null
    context?: Record<string, any>
  }
}): JSX.Element | null {
  const clubId = Number(item.clubId || item.context?.clubId || 0)
  const virtualMeetInvitationId = Number(item.context?.virtualMeetInvitationId || 0)
  const [loading, setLoading] = useState(false)
  const [invitation, setInvitation] = useState<ISharedVirtualMeetInvitationCardData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const missingIds = !clubId || !virtualMeetInvitationId

  const loadInvitationPreview = useCallback(async () => {
    if (missingIds) {
      setInvitation(null)
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response: any = await swimminglyApi.get(
        gen(`/api/getVisibleVirtualMeets?clubId=${clubId}`),
      )
      const visibleVirtualMeets: ISharedVirtualMeetInvitationCardData[] = Array.isArray(
        response?.virtualMeets,
      )
        ? response.virtualMeets
        : []

      const matchedInvitation =
        visibleVirtualMeets.find(
          (virtualMeet: any) =>
            Number(virtualMeet.virtualMeetInvitationId) === virtualMeetInvitationId &&
            virtualMeet.status === "pending",
        ) || null

      if (!matchedInvitation) {
        setInvitation(null)
        setError("Unable to load this virtual meet invitation preview.")
        return
      }

      setInvitation(matchedInvitation)
    } catch (err: any) {
      setInvitation(null)
      setError(err?.message || "Unable to load this virtual meet invitation preview.")
    } finally {
      setLoading(false)
    }
  }, [clubId, missingIds, virtualMeetInvitationId])

  useEffect(() => {
    void loadInvitationPreview()
  }, [loadInvitationPreview])

  useEffect(() => {
    if (missingIds) {
      return
    }

    const handleRefresh = () => {
      void loadInvitationPreview()
    }

    window.addEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)

    return () => {
      window.removeEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)
    }
  }, [loadInvitationPreview, missingIds])

  if (missingIds) {
    return (
      <Alert
        showIcon
        type="info"
        title="Open this virtual meet invitation from the action button below."
      />
    )
  }

  if (loading) {
    return <Skeleton active paragraph={{ rows: 6 }} />
  }

  if (error) {
    return <Alert showIcon type="info" title={error} />
  }

  if (!invitation) {
    return null
  }

  return <SharedVirtualMeetInvitationCard invitation={invitation} showReviewButton={false} />
}